import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clapperboard, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/billing/session/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          await checkAuth();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        if (n >= MAX_POLLS) { setState("timeout"); return; }
        setTimeout(() => { setAttempt(n + 1); poll(n + 1); }, 2000);
      } catch {
        if (n >= MAX_POLLS) { setState("timeout"); return; }
        setTimeout(() => { setAttempt(n + 1); poll(n + 1); }, 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0D0F12] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-[#14181F] border border-[#28303F] rounded-2xl p-8 text-center" data-testid="payment-success-card">
        <div className="flex items-center justify-center gap-2 mb-6 text-gray-500">
          <Clapperboard size={18} className="text-amber-400" />
          <span className="font-[Outfit] font-bold text-white">Get2Set</span>
        </div>
        {state === "polling" && (
          <>
            <Loader2 size={44} className="mx-auto text-amber-400 animate-spin" />
            <h1 className="mt-4 text-xl font-bold">Confirming your payment…</h1>
            <p className="text-sm text-gray-400 mt-2">Hang tight. This can take a few seconds. (attempt {attempt + 1}/{MAX_POLLS + 1})</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle size={44} className="mx-auto text-emerald-400" />
            <h1 className="mt-4 text-xl font-bold">You're in.</h1>
            <p className="text-sm text-gray-400 mt-2">Subscription active — thanks for backing Get2Set.</p>
            <button
              data-testid="payment-success-continue"
              onClick={() => navigate("/dashboard")}
              className="mt-6 w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold py-2.5 rounded-lg"
            >
              Open my dashboard
            </button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <AlertCircle size={44} className="mx-auto text-amber-400" />
            <h1 className="mt-4 text-xl font-bold">Still processing</h1>
            <p className="text-sm text-gray-400 mt-2">
              Your payment is on the way. Refresh this page in a minute, or head back to the dashboard — we'll flip you to active as soon as Stripe confirms.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-6 w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold py-2.5 rounded-lg"
            >
              Back to dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
